import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';

import { appsignal } from '../appsignal';
import { getProbeResult, ProbeResult } from './connectivity-probe';

type LikelyCause =
  | 'cors_or_aborted'
  | 'cert_or_network'
  | 'offline'
  | 'pending'
  | 'server_error'
  | 'client_error'
  | 'unknown';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const startedAt = performance.now();

    return next.handle(req).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.addHttpBreadcrumb(req, event.status, startedAt);
          }
        },
      }),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          this.addHttpBreadcrumb(req, err.status, startedAt, err.statusText);
          void this.reportHttpError(req, err, startedAt);
        }
        return throwError(() => err);
      }),
    );
  }

  private addHttpBreadcrumb(
    req: HttpRequest<unknown>,
    status: number,
    startedAt: number,
    statusText?: string,
  ): void {
    appsignal.addBreadcrumb({
      category: 'http',
      action: `${req.method} ${this.safePath(req.urlWithParams)}`,
      metadata: {
        status: String(status),
        status_text: statusText ?? '',
        duration_ms: String(Math.round(performance.now() - startedAt)),
      },
    });
  }

  private async reportHttpError(
    req: HttpRequest<unknown>,
    err: HttpErrorResponse,
    startedAt: number,
  ): Promise<void> {
    let probeResult: ProbeResult = 'unknown';
    let likelyCause: LikelyCause;

    if (err.status === 0) {
      if (!navigator.onLine) {
        likelyCause = 'offline';
        probeResult = 'offline';
      } else {
        const origin = this.safeOrigin(req.urlWithParams);
        probeResult = origin ? await getProbeResult(origin) : 'unknown';
        likelyCause = mapProbeToCause(probeResult);
      }
    } else if (err.status >= 500) {
      likelyCause = 'server_error';
    } else if (err.status >= 400) {
      likelyCause = 'client_error';
    } else {
      likelyCause = 'unknown';
    }

    const duration = Math.round(performance.now() - startedAt);
    const { name, reason } = buildErrorIdentity(err, likelyCause);

    const wrapped = new Error(`${req.method} ${req.urlWithParams} — ${reason}`);
    wrapped.name = name;

    appsignal.sendError(wrapped, (span) => {
      span.setAction(`${req.method} ${this.safePath(req.urlWithParams)}`);
      span.setNamespace('frontend.http');
      span.setTags({
        method: req.method,
        url: req.urlWithParams,
        status: String(err.status),
        status_text: err.statusText ?? '',
        duration_ms: String(duration),
        request_id: req.headers.get('X-Request-Id') ?? '',
        online: String(navigator.onLine),
        protocol: req.url.startsWith('https:') ? 'https' : 'http',
        probe_result: probeResult,
        likely_cause: likelyCause,
      });
    });
  }

  private safePath(url: string): string {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }

  private safeOrigin(url: string): string | null {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }
}

function mapProbeToCause(result: ProbeResult): LikelyCause {
  switch (result) {
    case 'reachable':
      return 'cors_or_aborted';
    case 'unreachable':
      return 'cert_or_network';
    case 'offline':
      return 'offline';
    case 'pending':
    case 'unknown':
      return 'pending';
  }
}

function buildErrorIdentity(
  err: HttpErrorResponse,
  likelyCause: LikelyCause,
): { name: string; reason: string } {
  if (err.status !== 0) {
    return {
      name: `Http${err.status}Error`,
      reason: `HTTP ${err.status} ${err.statusText}`,
    };
  }
  switch (likelyCause) {
    case 'cors_or_aborted':
      return {
        name: 'HttpCorsOrAbortError',
        reason: 'CORS, aborted, or blocked (no response)',
      };
    case 'cert_or_network':
      return {
        name: 'HttpCertOrNetworkError',
        reason: 'TLS/cert or network unreachable (no response)',
      };
    case 'offline':
      return { name: 'HttpOfflineError', reason: 'Offline' };
    default:
      return {
        name: 'HttpNetworkError',
        reason: 'Network failure, cause unknown (no response)',
      };
  }
}
